import React, { useEffect, useState, useCallback } from 'react';
import { Route } from '../routes';
import Container from '../components/ui/Container/Container';
import PageHeading from '../components/ui/PageHeading/PageHeading';
import { useAuditTrailService } from '../services/audit-trails-service';
import IAuditTrail from '../shared/IAuditTrail';
import Table from '../components/ui/Table/Table';
import Spinner from '../components/ui/Spinner/Spinner';
import Accordion from '../components/ui/Accordion/Accordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import '../components/AuditTrail/AuditTrail.scss';

const AuditTrailPage: React.FC = () => {
  const [auditTrails, setAuditTrails] = useState<IAuditTrail[]>([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState('');
  const [action, setAction] = useState('');
  const [limit, setLimit] = useState(100);
  
  const { fetchAuditTrails } = useAuditTrailService();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAuditTrails({ kind, action, limit });
      setAuditTrails(data);
    } catch (error) {
      console.error("Failed to fetch audit trails", error);
    } finally {
      setLoading(false);
    }
  }, [kind, action, limit, fetchAuditTrails]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <PageHeading backTo={Route.settings}>Audit Trails</PageHeading>
      <Container className="audit-trail-container">
        <div className="audit-trail-filters">
          <div className="filter-group">
            <label>Entity Kind</label>
            <select 
              value={kind} 
              onChange={(e) => setKind(e.target.value)}
            >
              <option value="">All Kinds</option>
              <option value="Organization">Organization</option>
              <option value="User">User</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Action</label>
            <select 
              value={action} 
              onChange={(e) => setAction(e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="SAVE_NEW">SAVE_NEW</option>
              <option value="UPDATE">UPDATE</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Limit</label>
            <select 
              value={limit} 
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>

          <button onClick={load} className="refresh-button">
            Refresh
          </button>
        </div>

        {loading ? (
          <Spinner />
        ) : auditTrails.length === 0 ? (
          <div className="no-history">
            No history found matching your filters.
          </div>
        ) : (
          <div className="audit-trail-list-container">
            <div className="audit-trail-list-header page-layout">
              <div className="column-header">Kind</div>
              <div className="column-header">Target</div>
              <div className="column-header">Action</div>
              <div className="column-header">Operator</div>
              <div className="column-header">Date</div>
              <div className="column-header">Changes</div>
            </div>
            <div className="audit-trail-list-body">
              {auditTrails.map((item) => {
                const hasChanges = item.changes && item.changes.length > 0;
                const rowHeader = (
                  <div className="audit-trail-row-header page-layout">
                    <div className="header-cell">{item.kind}</div>
                    <div className="header-cell target-name">{item.targetName}</div>
                    <div className="header-cell">
                      <span className={`action-tag action-${item.action.toLowerCase().replace('_', '-')}`}>
                        {item.action}
                      </span>
                    </div>
                    <div className="header-cell">
                      {item.actionUser?.name || item.actionUser?.username || item.actionUser?.email || <span className="system-operator">System</span>}
                    </div>
                    <div className="header-cell">{item.created}</div>
                    <div className="header-cell changes-summary">
                      {hasChanges ? `${item.changes.length} changes` : '-'}
                    </div>
                  </div>
                );

                return (
                  <div key={item.id} className="audit-trail-list-item">
                    <Accordion text={rowHeader} withContentPadding>
                      {hasChanges ? (
                        <Table condensed style={{ margin: 0, width: '100%', borderTop: 0 }}>
                          <thead>
                            <tr>
                              <th>Field</th>
                              <th>Old Value</th>
                              <th style={{ textAlign: 'center' }}></th>
                              <th>New Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.changes.map((change, index) => (
                              <tr key={index}>
                                <td style={{ fontWeight: 500 }}>{change.fieldName}</td>
                                <td>{change.oldValue || '-'}</td>
                                <td style={{ textAlign: 'center', color: '#ccc' }}>
                                  <FontAwesomeIcon icon={faArrowRight} />
                                </td>
                                <td style={{ fontWeight: 500 }}>{change.newValue || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div style={{ padding: '1rem', color: '#888' }}>No changes recorded.</div>
                      )}
                    </Accordion>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

export default AuditTrailPage;
